import useApi from 'assets/hooks/api/useApi';
import usePageRouter from 'assets/hooks/pageRouter/usePageRouter';
import { useAuthStore } from 'assets/providers/authStore/Provider.AuthStore';
import { useDataProvider } from 'assets/providers/data/DataProvider';
import PricingDistributionMasterTemplate, {
  pricingDistributionMasterTemplateSaveApi,
} from 'models/productManagement/pricingDistributionMasterTemplate/Model.PricingDistributionMasterTemplate';
import { useMemo } from 'react';
import { pricingDistributionMasterTemplateIndexRoute } from './PricingDistributionMasterTemplate.Index';

export const PRICING_DISTRIBUTION_TEMPLATE_SEPARATOR = '.';

export enum PricingDistributionMasterTemplateAction {
  EDIT = 'edit',
}
export type VerifyPricingDistributionMasterTemplateAction = Utils.VerifyExtends<
  Module.ProductManagement.PricingDistributionMasterTemplate.Actions,
  Utils.ValueOf<typeof PricingDistributionMasterTemplateAction>
>;
export default function usePricingDistributionMasterTemplate() {
  const { permissions, user, isDealerOrDistributor } = useAuthStore();
  const pageRouter = usePageRouter<
    Module.ProductManagement.PricingDistributionMasterTemplate.Params,
    Module.ProductManagement.PricingDistributionMasterTemplate.Query
  >({ route: pricingDistributionMasterTemplateIndexRoute });

  const canView = permissions.ACCESS_PRICINGDISTRIBUTIONMASTERTEMPLATE;
  const listApi = useApi({
    action: PricingDistributionMasterTemplate.list,
    callback: (req) => pageRouter.redirect({}, { ...pageRouter.query, list: req }),
    wait: !canView,
    body: !user?.distributorId
      ? null
      : {
          distributorId: [user?.distributorId],
        },
  });

  const selected = useMemo(() => {
    return listApi.payload?.data?.find((it) => it.id.toString() === pageRouter.params.id?.toString());
  }, [listApi.payload?.data, pageRouter.params.id]);

  const saveApi = useApi({
    action: pricingDistributionMasterTemplateSaveApi,
    callback: () => listApi.execute((body) => body),
    wait: true,
  });

  return {
    canView,
    saveApi,
    selected,
    pageRouter,
    listApi,
    isDealerOrDistributor,
    permissions,
    views: {
      [PricingDistributionMasterTemplateAction.EDIT]:
        pageRouter.params.action === PricingDistributionMasterTemplateAction.EDIT &&
        permissions.ACCESS_PRICINGDISTRIBUTIONMASTERTEMPLATE,
    },
  };
}

export function usePricingDistributionMasterTemplateProvider() {
  return useDataProvider<ReturnType<typeof usePricingDistributionMasterTemplate>>();
}
